import type { ApiResponse, UploadResponse, Session, SessionCreateResponse, SessionRenewResponse, SessionTimeRemaining, Submission, FormData, GeneratedDocument, SigningSession, SignatureRecord, Prodotto } from '@/types'
import { compressImage, compressFile, formatFileSize } from '@/utils/imageCompression'

// API Configuration
const API_BASE = '/backend/api/v1'

// Soglia per compressione automatica (3MB)
const COMPRESSION_THRESHOLD_BYTES = 3 * 1024 * 1024

// Timeout di default per le richieste API (30 secondi)
const DEFAULT_TIMEOUT_MS = 30000

// Timeout lungo per operazioni pesanti come completeSigning (3 minuti)
const LONG_TIMEOUT_MS = 180000

// Helper per costruire URL
function apiUrl(endpoint: string): string {
  return `${API_BASE}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`
}

// Gestione errori
class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errors: Record<string, string> = {}
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Wrapper per fetch con timeout
 * @param url URL da chiamare
 * @param options Opzioni fetch
 * @param timeoutMs Timeout in millisecondi
 */
async function fetchWithTimeout(
  url: string, 
  options: RequestInit = {}, 
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    })
    return response
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(`Richiesta scaduta dopo ${timeoutMs / 1000} secondi`, 408)
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

// Fetch wrapper con gestione errori
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<ApiResponse<T>> {
  const url = apiUrl(endpoint)
  
  const defaultHeaders: HeadersInit = {
    'Accept': 'application/json',
  }
  
  // Non impostare Content-Type per FormData (browser lo gestisce)
  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json'
  }
  
  const response = await fetchWithTimeout(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include',
  }, timeoutMs)
  
  let data: ApiResponse<T>
  
  try {
    data = await response.json()
  } catch {
    throw new ApiError('Risposta non valida dal server', response.status)
  }
  
  if (!response.ok || !data.success) {
    throw new ApiError(
      data.message || 'Errore durante la richiesta',
      response.status,
      data.errors
    )
  }
  
  return data
}

// ============================================
// SESSION API
// ============================================

export const sessionApi = {
  /**
   * Crea nuova sessione con supporto GDPR
   * @param mode Modalità sessione: 'self' (default, 10 min timeout) o 'operator' (8h timeout)
   */
  async create(mode: 'self' | 'operator' = 'self'): Promise<SessionCreateResponse> {
    const response = await apiFetch<SessionCreateResponse>('/sessions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mode }),
    })
    return response.data
  },
  
  /**
   * Ottieni sessione esistente con tempo rimanente
   * Questa chiamata RINNOVA automaticamente la scadenza
   */
  async get(id: string): Promise<Session> {
    const response = await apiFetch<{ session: Session }>(`/sessions/${id}`)
    return response.data.session
  },
  
  /**
   * Ottieni solo tempo rimanente (endpoint leggero per polling)
   * Questa chiamata NON rinnova la scadenza
   */
  async getTimeRemaining(id: string): Promise<SessionTimeRemaining> {
    const response = await apiFetch<{ 
      session_id: string
      time_remaining: SessionTimeRemaining
      expires_at: string 
    }>(`/sessions/${id}/time`)
    return response.data.time_remaining
  },
  
  /**
   * Rinnova scadenza sessione (heartbeat)
   * Da chiamare ad ogni attività utente
   */
  async renew(id: string): Promise<SessionRenewResponse> {
    const response = await apiFetch<SessionRenewResponse>(`/sessions/${id}/renew`, {
      method: 'PUT',
    })
    return response.data
  },
  
  /**
   * Termina sessione volontariamente
   */
  async end(id: string): Promise<void> {
    await apiFetch(`/sessions/${id}/end`, {
      method: 'PUT',
    })
  },
}

// ============================================
// UPLOAD API
// ============================================

export const uploadApi = {
  /**
   * Carica file (PDF o immagine)
   * Comprime automaticamente le immagini troppo grandi
   */
  async upload(file: File): Promise<UploadResponse> {
    let fileToUpload = file
    
    // Comprimi se è un'immagine troppo grande
    if (file.type.startsWith('image/') && file.size > COMPRESSION_THRESHOLD_BYTES) {
      console.log(`[Upload] Compressione immagine: ${formatFileSize(file.size)}`)
      fileToUpload = await compressFile(file, {
        maxDimension: 1920,
        quality: 0.8,
        maxSizeBytes: COMPRESSION_THRESHOLD_BYTES
      })
      console.log(`[Upload] Dopo compressione: ${formatFileSize(fileToUpload.size)}`)
    }
    
    const formData = new FormData()
    formData.append('file', fileToUpload)
    
    const response = await apiFetch<UploadResponse>('/upload', {
      method: 'POST',
      body: formData,
    })
    
    return response.data
  },
  
  /**
   * Carica immagine da canvas (per camera)
   * Comprime automaticamente le immagini troppo grandi
   */
  async uploadBlob(blob: Blob, filename: string = 'camera-capture.jpg'): Promise<UploadResponse> {
    let blobToUpload = blob
    
    // Comprimi se è troppo grande
    if (blob.size > COMPRESSION_THRESHOLD_BYTES) {
      console.log(`[Upload] Compressione blob: ${formatFileSize(blob.size)}`)
      const result = await compressImage(blob, {
        maxDimension: 1920,
        quality: 0.8,
        maxSizeBytes: COMPRESSION_THRESHOLD_BYTES
      })
      blobToUpload = result.blob
      console.log(`[Upload] Dopo compressione: ${formatFileSize(blobToUpload.size)} (ratio: ${result.compressionRatio.toFixed(2)}x)`)
    }
    
    const formData = new FormData()
    formData.append('file', blobToUpload, filename)
    
    const response = await apiFetch<UploadResponse>('/upload', {
      method: 'POST',
      body: formData,
    })
    
    return response.data
  },
  
  /**
   * Carica multiple immagini e unisce i dati estratti
   * Comprime automaticamente le immagini troppo grandi
   */
  async uploadMultipleBlobs(
    blobs: Blob[], 
    onProgress?: (current: number, total: number) => void
  ): Promise<UploadResponse> {
    const results: UploadResponse[] = []
    
    for (let i = 0; i < blobs.length; i++) {
      let blob = blobs[i]
      const filename = `camera-capture-${i + 1}.jpg`
      
      // Comprimi se è troppo grande
      if (blob.size > COMPRESSION_THRESHOLD_BYTES) {
        console.log(`[Upload Multi] Compressione blob ${i + 1}: ${formatFileSize(blob.size)}`)
        const result = await compressImage(blob, {
          maxDimension: 1920,
          quality: 0.8,
          maxSizeBytes: COMPRESSION_THRESHOLD_BYTES
        })
        blob = result.blob
        console.log(`[Upload Multi] Dopo compressione: ${formatFileSize(blob.size)}`)
      }
      
      onProgress?.(i + 1, blobs.length)
      
      const formData = new FormData()
      formData.append('file', blob, filename)
      
      const response = await apiFetch<UploadResponse>('/upload', {
        method: 'POST',
        body: formData,
      })
      
      results.push(response.data)
    }
    
    // Unisci i dati estratti da tutte le immagini
    return mergeUploadResults(results)
  },
  
  /**
   * Carica documento d'identità (carta d'identità, passaporto, patente)
   * Comprime automaticamente le immagini troppo grandi
   * @param file - File da caricare
   * @param imageType - 'fronte' | 'retro' | 'completo' - lato del documento
   */
  async uploadIdentity(file: File, imageType: 'fronte' | 'retro' | 'completo' = 'fronte', submissionId?: number | null): Promise<UploadResponse & { tipo_documento?: string; image_type?: string }> {
    let fileToUpload = file
    
    // Comprimi se è un'immagine troppo grande (limite backend: 5MB)
    const IDENTITY_MAX_SIZE = 5 * 1024 * 1024 // 5MB per documenti d'identità
    if (file.type.startsWith('image/') && file.size > IDENTITY_MAX_SIZE) {
      console.log(`[Upload Identity] Compressione immagine: ${formatFileSize(file.size)}`)
      fileToUpload = await compressFile(file, {
        maxDimension: 1920,
        quality: 0.85, // Qualità leggermente più alta per documenti
        maxSizeBytes: IDENTITY_MAX_SIZE
      })
      console.log(`[Upload Identity] Dopo compressione: ${formatFileSize(fileToUpload.size)}`)
    }
    
    const formData = new FormData()
    formData.append('file', fileToUpload)
    formData.append('image_type', imageType)
    if (submissionId) {
      formData.append('submission_id', String(submissionId))
    }
    
    const response = await apiFetch<UploadResponse & { tipo_documento?: string; image_type?: string }>('/upload-identity', {
      method: 'POST',
      body: formData,
    })
    
    return response.data
  },
  
  /**
   * Carica documento d'identità da blob (per camera)
   * Comprime automaticamente le immagini troppo grandi
   * @param blob - Blob immagine
   * @param filename - Nome file
   * @param imageType - 'fronte' | 'retro' | 'completo' - lato del documento
   */
  async uploadIdentityBlob(blob: Blob, filename: string = 'identity-document.jpg', imageType: 'fronte' | 'retro' | 'completo' = 'fronte', submissionId?: number | null): Promise<UploadResponse & { tipo_documento?: string; image_type?: string }> {
    let blobToUpload = blob
    
    // Comprimi se è troppo grande (limite backend: 5MB)
    const IDENTITY_MAX_SIZE = 5 * 1024 * 1024 // 5MB per documenti d'identità
    if (blob.size > IDENTITY_MAX_SIZE) {
      console.log(`[Upload Identity Blob] Compressione blob: ${formatFileSize(blob.size)}`)
      const result = await compressImage(blob, {
        maxDimension: 1920,
        quality: 0.85, // Qualità leggermente più alta per documenti
        maxSizeBytes: IDENTITY_MAX_SIZE
      })
      blobToUpload = result.blob
      console.log(`[Upload Identity Blob] Dopo compressione: ${formatFileSize(blobToUpload.size)}`)
    }
    
    const formData = new FormData()
    formData.append('file', blobToUpload, filename)
    formData.append('image_type', imageType)
    if (submissionId) {
      formData.append('submission_id', String(submissionId))
    }
    
    const response = await apiFetch<UploadResponse & { tipo_documento?: string; image_type?: string }>('/upload-identity', {
      method: 'POST',
      body: formData,
    })
    
    return response.data
  },
  
  /**
   * Carica multiple immagini di documento d'identità (fronte/retro)
   * Comprime automaticamente le immagini troppo grandi
   * Le immagini vengono combinate in un PDF e elaborate insieme
   */
  async uploadIdentityMultiple(
    blobs: Blob[],
    onProgress?: (current: number, total: number) => void
  ): Promise<UploadResponse & { tipo_documento?: string }> {
    const formData = new FormData()
    
    // Comprimi e aggiungi tutte le immagini come array
    const IDENTITY_MAX_SIZE = 5 * 1024 * 1024 // 5MB per documenti d'identità
    for (let i = 0; i < blobs.length; i++) {
      let blob = blobs[i]
      
      // Comprimi se è troppo grande
      if (blob.size > IDENTITY_MAX_SIZE) {
        console.log(`[Upload Identity Multi] Compressione blob ${i + 1}: ${formatFileSize(blob.size)}`)
        const result = await compressImage(blob, {
          maxDimension: 1920,
          quality: 0.85, // Qualità leggermente più alta per documenti
          maxSizeBytes: IDENTITY_MAX_SIZE
        })
        blob = result.blob
        console.log(`[Upload Identity Multi] Dopo compressione: ${formatFileSize(blob.size)}`)
      }
      
      formData.append('files[]', blob, `identity-document-${i + 1}.jpg`)
      onProgress?.(i + 1, blobs.length)
    }
    
    const response = await apiFetch<UploadResponse & { tipo_documento?: string }>('/upload-identity-multiple', {
      method: 'POST',
      body: formData,
    })
    
    return response.data
  },
}

// ============================================
// IDENTITY DOCUMENTS API
// ============================================

import type { IdentityDocumentsStatus } from '@/types'

export const identityDocumentsApi = {
  /**
   * Ottieni stato documenti identità per la sessione corrente.
   *
   * Quando l'operatore sta editando una submission esistente (submissionId
   * presente nello store), passiamo l'id come query string cosi' il backend
   * restituisce anche i documenti gia' legati a quella submission. Senza
   * questo parametro, il backend restituisce solo i documenti "pending"
   * (submission_id IS NULL) della sessione corrente.
   */
  async getStatus(submissionId?: number | null): Promise<IdentityDocumentsStatus> {
    const url = submissionId
      ? `/identity-documents?submission_id=${submissionId}`
      : '/identity-documents'
    const response = await apiFetch<IdentityDocumentsStatus>(url)
    return response.data
  },

  /**
   * Elimina i documenti identità "pending" della sessione corrente
   * (e quelli della submission corrente se specificata).
   */
  async deleteAll(submissionId?: number | null): Promise<void> {
    const url = submissionId
      ? `/identity-documents?submission_id=${submissionId}`
      : '/identity-documents'
    await apiFetch(url, {
      method: 'DELETE',
    })
  },
}

/** Elimina documenti identità lato server per la sessione HTTP corrente (idempotente). */
export async function clearIdentityDocumentsServerState(): Promise<void> {
  try {
    await identityDocumentsApi.deleteAll()
  } catch {
    /* sessione assente o rete: non bloccare avvio wizard */
  }
}

/**
 * Unisce i risultati di multiple estrazioni OCR
 * Priorità: dati più completi sovrascrivono quelli incompleti
 */
function mergeUploadResults(results: UploadResponse[]): UploadResponse {
  if (results.length === 0) {
    return {
      file_id: null,
      data: {} as UploadResponse['data'],
      extractor: 'multi-merge',
      fornitore: 'unknown',
      processing_time_ms: 0
    }
  }
  
  if (results.length === 1) {
    return results[0]
  }
  
  // Campi da unire con priorità
  const mergedData: Record<string, unknown> = {}
  let hasLuce = false
  let hasGas = false
  let bestExtractor = 'multi-merge'
  let bestFornitore = 'unknown'
  let totalProcessingTime = 0
  
  // Campi da unire
  const fieldsToMerge = [
    // Anagrafica
    'nome', 'cognome', 'codice_fiscale',
    // Indirizzo
    'indirizzo_residenza', 'cap_residenza', 'citta_residenza', 'provincia_residenza',
    'indirizzo_fornitura', 'cap_fornitura', 'citta_fornitura', 'provincia_fornitura',
    'indirizzo', 'cap', 'citta', 'provincia',
    // Luce
    'pod', 'pod_luce', 'potenza_impegnata', 'consumo_annuo_luce',
    // Gas
    'pdr', 'pdr_gas', 'matricola_contatore', 'remi_pdc', 'consumo_annuo_gas',
    // Altro
    'bolletta_type'
  ]
  
  for (let i = 0; i < results.length; i++) {
    const result = results[i]
    if (!result.data) continue
    
    // Accumula tempo di processing
    totalProcessingTime += result.processing_time_ms || 0
    
    // Aggiorna extractor e fornitore se trovati
    if (result.extractor && result.extractor !== 'multi-merge') {
      bestExtractor = result.extractor
    }
    if (result.fornitore && result.fornitore !== 'unknown') {
      bestFornitore = result.fornitore
    }
    
    // Verifica tipo bolletta
    if (result.data.bolletta_type === 'luce' || result.data.ha_luce) {
      hasLuce = true
    }
    if (result.data.bolletta_type === 'gas' || result.data.ha_gas) {
      hasGas = true
    }
    
    // Unisci campi (usa il primo valore non vuoto trovato)
    for (const field of fieldsToMerge) {
      const value = result.data[field as keyof typeof result.data]
      if (value && !mergedData[field]) {
        mergedData[field] = value
      }
    }
  }
  
  // Imposta ha_luce e ha_gas basandosi sui dati trovati
  if (hasLuce) {
    mergedData.ha_luce = true
  }
  if (hasGas) {
    mergedData.ha_gas = true
  }
  
  // Se abbiamo sia luce che gas, imposta bolletta_type
  if (hasLuce && hasGas) {
    mergedData.bolletta_type = 'entrambi'
  } else if (hasLuce) {
    mergedData.bolletta_type = 'luce'
  } else if (hasGas) {
    mergedData.bolletta_type = 'gas'
  }
  
  return {
    file_id: results[0]?.file_id || null,
    data: mergedData as UploadResponse['data'],
    extractor: bestExtractor,
    fornitore: bestFornitore,
    processing_time_ms: totalProcessingTime
  }
}

// ============================================
// SUBMISSIONS API
// ============================================

export const submissionsApi = {
  /**
   * Crea nuova submission
   */
  async create(data: Partial<FormData>): Promise<{ id: number; uuid: string; status: string }> {
    const response = await apiFetch<{ id: number; uuid: string; status: string }>('/submissions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data
  },
  
  /**
   * Ottieni submission
   */
  async get(id: number | string): Promise<Submission> {
    const response = await apiFetch<{ submission: Submission }>(`/submissions/${id}`)
    return response.data.submission
  },
  
  /**
   * Aggiorna submission
   */
  async update(id: number | string, data: Partial<FormData>): Promise<Submission> {
    const response = await apiFetch<{ submission: Submission }>(`/submissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return response.data.submission
  },
  
  /**
   * Invia submission finale
   * Restituisce la submission e i documenti PDF generati
   */
  async submit(id: number | string): Promise<{ submission: Submission; documents?: GeneratedDocument[] }> {
    const response = await apiFetch<{ submission: Submission; documents?: GeneratedDocument[] }>(`/submissions/${id}/submit`, {
      method: 'POST',
    })
    return response.data
  },
  
  /**
   * Prepara submission per invio operatore (valida ma non completa)
   * Usato nel flusso operatore: valida i dati e imposta status 'pending'
   * Genera anche i PDF per la firma del cliente
   */
  async prepare(id: number | string): Promise<{ submission: Submission; documents?: GeneratedDocument[] }> {
    const response = await apiFetch<{ submission: Submission; documents?: GeneratedDocument[] }>(`/submissions/${id}/prepare`, {
      method: 'POST',
    })
    return response.data
  },
  
  /**
   * Annulla submission
   */
  async cancel(id: number | string): Promise<void> {
    await apiFetch(`/submissions/${id}`, {
      method: 'DELETE',
    })
  },
}

// ============================================
// BUSINESS SUBMISSIONS API
// ============================================

export const businessSubmissionsApi = {
  async create(data: Partial<FormData>): Promise<{ id: number; uuid: string; status: string }> {
    const response = await apiFetch<{ id: number; uuid: string; status: string }>('/submissions', {
      method: 'POST',
      body: JSON.stringify({ ...data, tipo_cliente: 'business' }),
    })
    return response.data
  },

  async update(id: number | string, data: Partial<FormData>): Promise<Submission> {
    const response = await apiFetch<{ submission: Submission }>(`/submissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...data, tipo_cliente: 'business' }),
    })
    return response.data.submission
  },

  async getLatestBySession(): Promise<Submission | null> {
    try {
      const response = await apiFetch<{ submission: Submission | null }>('/submissions?tipo_cliente=business&latest=1')
      return response.data.submission
    } catch {
      return null
    }
  },
}

// ============================================
// PRODOTTI API
// ============================================

export const prodottiApi = {
  /**
   * Lista prodotti (filtro opzionale commodity, attivo)
   */
  async getProdotti(params?: {
    commodity?: 'luce' | 'gas'
    attivo?: boolean
    tipo_cliente?: 'domestico' | 'business'
  }): Promise<Prodotto[]> {
    const qs = new URLSearchParams()
    if (params?.commodity) qs.set('commodity', params.commodity)
    if (params?.attivo !== undefined) qs.set('attivo', String(params.attivo ? 1 : 0))
    if (params?.tipo_cliente) qs.set('tipo_cliente', params.tipo_cliente)
    const query = qs.toString()
    const response = await apiFetch<{ prodotti: Prodotto[] }>(`/prodotti${query ? '?' + query : ''}`)
    return response.data.prodotti
  },

  /**
   * Dettaglio prodotto
   */
  async getProdotto(id: number): Promise<Prodotto> {
    const response = await apiFetch<{ prodotto: Prodotto }>(`/prodotti/${id}`)
    return response.data.prodotto
  },
}

// ============================================
// ANALYTICS API
// ============================================

export type EventType = 
  | 'page_view' 
  | 'form_start' 
  | 'form_step' 
  | 'form_field' 
  | 'upload' 
  | 'camera' 
  | 'submit' 
  | 'error' 
  | 'interaction'

export const analyticsApi = {
  /**
   * Traccia evento
   */
  async track(
    eventType: EventType,
    eventName: string,
    data: Record<string, unknown> = {}
  ): Promise<void> {
    try {
      await apiFetch('/analytics', {
        method: 'POST',
        body: JSON.stringify({
          event_type: eventType,
          event_name: eventName,
          page_url: window.location.href,
          page_title: document.title,
          ...data,
        }),
      })
    } catch {
      // Non fallire per errori analytics
      console.warn('Analytics tracking failed')
    }
  },
  
  /**
   * Traccia visualizzazione pagina
   */
  async trackPageView(pageName: string): Promise<void> {
    return this.track('page_view', pageName)
  },
  
  /**
   * Traccia step form
   */
  async trackFormStep(step: string, stepNumber: number): Promise<void> {
    return this.track('form_step', step, { form_step: stepNumber })
  },
  
  /**
   * Traccia campo form
   */
  async trackField(fieldName: string): Promise<void> {
    return this.track('form_field', `field_${fieldName}`, { field_name: fieldName })
  },
  
  /**
   * Traccia errore
   */
  async trackError(errorMessage: string, errorCode?: string): Promise<void> {
    return this.track('error', 'app_error', {
      data: { error: errorMessage, code: errorCode },
    })
  },
}

// ============================================
// COSTI SUBENTRO API
// ============================================

export interface CostiSubentroResponse {
  gas: {
    titolo: string
    descrizione: string
    importo: number
    iva_esclusa: boolean
    importo_formattato: string
  }
  luce: {
    titolo: string
    base: {
      descrizione: string
      importo: number
      iva_esclusa: boolean
      importo_formattato: string
    }
    aumento_potenza: {
      descrizione: string
      importo_per_kw: number
      importo_formattato: string
    }
    soglia_preventivo: number
    nota_soglia: string
  }
  note_legali: string
  nota_recesso: string
}

export const costiSubentroApi = {
  /**
   * Ottieni costi subentro
   */
  async get(): Promise<CostiSubentroResponse> {
    const response = await apiFetch<CostiSubentroResponse>('/costi-subentro')
    return response.data
  },
}

// ============================================
// COSTI VOLTURA API
// ============================================

export interface CostiVolturaVoce {
  descrizione: string
  importo: number
  iva_esclusa: boolean
  importo_formattato: string
  nota: string | null
}

export interface CostiVolturaResponse {
  gas: {
    titolo: string
    voci: CostiVolturaVoce[]
  }
  luce: {
    titolo: string
    voci: CostiVolturaVoce[]
    nota_voltura: string | null
  }
  note_legali: string
  nota_recesso: string
}

export const costiVolturaApi = {
  /**
   * Ottieni costi voltura
   */
  async get(): Promise<CostiVolturaResponse> {
    const response = await apiFetch<CostiVolturaResponse>('/costi-voltura')
    return response.data
  },
}

// ============================================
// GENERATE PDF API
// ============================================

import type { GeneratePdfResponse } from '@/types'

export const generatePdfApi = {
  /**
   * Genera tutti i PDF per una submission
   */
  async generate(submissionId: number): Promise<GeneratePdfResponse> {
    const response = await apiFetch<GeneratePdfResponse>('/generate-pdf', {
      method: 'POST',
      body: JSON.stringify({ submission_id: submissionId }),
    })
    return response.data
  },

  /**
   * Genera un singolo PDF
   */
  async generateSingle(
    submissionId: number,
    templateType: 'subentro' | 'voltura' | 'cambio_fornitore',
    utility: 'luce' | 'gas'
  ): Promise<GeneratedDocument> {
    const response = await apiFetch<{ documents: GeneratedDocument[] }>('/generate-pdf', {
      method: 'POST',
      body: JSON.stringify({
        submission_id: submissionId,
        template_type: templateType,
        utility: utility,
      }),
    })
    return response.data.documents[0]
  },

  /**
   * Ottieni URL di download per un documento
   */
  getDownloadUrl(filename: string): string {
    return `${API_BASE}/generate-pdf/${encodeURIComponent(filename)}`
  },
}

// ============================================
// CODICE FISCALE API
// ============================================

export interface DecodificaCFResponse {
  codice_fiscale: string
  data_nascita: string | null
  data_nascita_formato_ita: string | null
  sesso: 'M' | 'F' | null
  luogo_nascita: string | null
  provincia_nascita: string | null
  codice_catastale: string | null
}

export interface DecodificaCFEsito {
  data: DecodificaCFResponse | null
  /** Motivo del rifiuto, da mostrare all'operatore al posto di un generico "non valido". */
  message: string | null
}

export const codiceFiscaleApi = {
  /**
   * Decodifica codice fiscale ed estrae data e luogo di nascita
   */
  async decodifica(codiceFiscale: string): Promise<DecodificaCFEsito> {
    try {
      const response = await fetch(`/backend/api/decodifica-codice-fiscale.php?codice_fiscale=${encodeURIComponent(codiceFiscale)}`)
      const data = await response.json()
      
      if (data.success && data.data) {
        return { data: data.data as DecodificaCFResponse, message: null }
      }
      return { data: null, message: typeof data.message === 'string' ? data.message : null }
    } catch {
      console.warn('Errore decodifica codice fiscale')
      return { data: null, message: null }
    }
  },
}

// ============================================
// SIGNING API
// ============================================

export interface CreateSigningSessionParams {
  submission_id: number
  contact_type: 'email' | 'phone'
  document_name?: string
  pdf_url?: string
}

export interface OtpRequestResponse {
  message: string
  expires_at: string
  contact_masked: string
  _debug_code?: string
}

export interface OtpVerifyResponse {
  verified: boolean
  message: string
}

export interface SignFieldResponse {
  signature: SignatureRecord
}

export interface CompleteSigningResponse {
  document_id: string
  signatures: SignatureRecord[]
  completed_at: string
}

export const signingApi = {
  /**
   * Crea sessione di firma
   */
  async createSession(params: CreateSigningSessionParams): Promise<{ session: SigningSession }> {
    const response = await apiFetch<{ session: SigningSession }>('/signing/session', {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return response.data
  },

  /**
   * Ottieni stato sessione
   */
  async getSession(sessionId: string): Promise<{ session: SigningSession; signatures: SignatureRecord[] }> {
    const response = await apiFetch<{ session: SigningSession; signatures: SignatureRecord[] }>(`/signing/session/${sessionId}`)
    return response.data
  },

  /**
   * Richiedi invio OTP
   */
  async requestOtp(sessionId: string): Promise<OtpRequestResponse> {
    const response = await apiFetch<OtpRequestResponse>('/signing/otp/request', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    })
    return response.data
  },

  /**
   * Verifica codice OTP
   */
  async verifyOtp(sessionId: string, code: string): Promise<OtpVerifyResponse> {
    const response = await apiFetch<OtpVerifyResponse>('/signing/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, code }),
    })
    return response.data
  },

  /**
   * Firma un campo
   */
  async signField(sessionId: string, fieldId: string, fieldName: string): Promise<SignFieldResponse> {
    const response = await apiFetch<SignFieldResponse>('/signing/sign', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        field_id: fieldId,
        field_name: fieldName,
      }),
    })
    return response.data
  },

  /**
   * Completa processo di firma
   * Usa timeout lungo (3 minuti) perche include generazione PDF e upload SiDial
   */
  async completeSigning(sessionId: string): Promise<CompleteSigningResponse> {
    const response = await apiFetch<CompleteSigningResponse>('/signing/complete', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    }, LONG_TIMEOUT_MS)
    return response.data
  },

  /**
   * Ottieni documento firmato
   */
  async getSignedDocument(sessionId: string): Promise<{
    session_id: string
    status?: string
    signatures: SignatureRecord[]
    completed: boolean
    document_id?: string
    completed_at?: string
  }> {
    const response = await apiFetch<{
      session_id: string
      status?: string
      signatures: SignatureRecord[]
      completed: boolean
      document_id?: string
      completed_at?: string
    }>(`/signing/document/${sessionId}`)
    return response.data
  },

  /**
   * Ottieni firme per sessione
   */
  async getSignatures(sessionId: string): Promise<{ signatures: SignatureRecord[] }> {
    const response = await apiFetch<{ signatures: SignatureRecord[] }>(`/signing/signatures/${sessionId}`)
    return response.data
  },
}

// ============================================
// COPERTURA COMUNI API
// ============================================

import type { CoperturaResponse, CoperturaSearchResponse, CoperturaComune } from '@/types'

export const coperturaApi = {
  /**
   * Verifica copertura per un comune
   * @param comune Nome del comune
   * @param provincia Sigla provincia (opzionale)
   * @param cap CAP (opzionale)
   */
  async verifica(comune: string, provincia?: string, cap?: string): Promise<CoperturaResponse> {
    const params = new URLSearchParams({ comune })
    if (provincia) params.append('provincia', provincia)
    if (cap) params.append('cap', cap)
    
    const response = await apiFetch<CoperturaResponse>(`/copertura-comuni?${params.toString()}`)
    return response.data
  },

  /**
   * Cerca comuni (autocomplete)
   * @param query Termine di ricerca (minimo 2 caratteri)
   * @param limit Numero massimo risultati (default 10)
   */
  async search(query: string, limit: number = 10): Promise<CoperturaComune[]> {
    if (query.length < 2) return []
    
    const params = new URLSearchParams({ q: query, limit: limit.toString() })
    const response = await apiFetch<CoperturaSearchResponse>(`/copertura-comuni/search?${params.toString()}`)
    return response.data.comuni
  },

  /**
   * Cerca comuni per CAP
   */
  async cercaPerCap(cap: string): Promise<CoperturaComune[]> {
    const response = await apiFetch<{ comuni: CoperturaComune[] }>(`/copertura-comuni/cap/${cap}`)
    return response.data.comuni
  },

  /**
   * Statistiche copertura
   */
  async getStats(): Promise<{
    totale_comuni: number
    comuni_luce: number
    comuni_gas: number
    comuni_entrambi: number
  }> {
    const response = await apiFetch<{
      totale_comuni: number
      comuni_luce: number
      comuni_gas: number
      comuni_entrambi: number
    }>('/copertura-comuni/stats')
    return response.data
  }
}

// ============================================
// OPERATOR API
// ============================================

import type { SendToClientResponse, OperatorSubmissionDetail, ClientSigningResponse, StartClientSigningResponse, PaginationInfo } from '@/types'

// Tipo per la lista pratiche operatore
export interface PraticaListItem {
  id: number
  uuid: string
  status: 'draft' | 'pending' | 'completed' | 'cancelled'
  tipo_cliente?: 'domestico' | 'business'
  tipo_fornitura: 'luce' | 'gas' | 'entrambi'
  anagrafica: {
    nome: string | null
    cognome: string | null
    codice_fiscale: string | null
    telefono: string | null
    email: string | null
  }
  operator_username: string | null
  tipo_attivazione_luce: string | null
  tipo_attivazione_gas: string | null
  signing?: {
    session_id: string | null
    signed_pdf_available: boolean
  }
  created_at: string
  updated_at: string
  submitted_at: string | null
}

export interface ListPraticheParams {
  status?: string
  search?: string
  tipo_cliente?: 'domestico' | 'business'
  from?: string
  to?: string
  page?: number
  per_page?: number
}

export interface ListPraticheResponse {
  data: PraticaListItem[]
  pagination: PaginationInfo
}

// Tipi per autenticazione operatore
export interface OperatorUser {
  id: number
  username: string
  full_name: string
  role: 'operatore' | 'supervisor' | 'admin' | 'backoffice' | 'bo_ameconviene'
  /** Fallback: true se il bollettino e' nascosto su almeno un segmento. Preferire `hide_bollettino_postale_tipi_cliente`. */
  hide_bollettino_postale?: boolean
  /** Segmenti cliente per cui nel form di caricamento va nascosta l'opzione "Bollettino PagoPA" (solo IBAN). */
  hide_bollettino_postale_tipi_cliente?: Array<'domestico' | 'business'>
  /** Se true, nel form pagamento e' consentito indicare un pagatore alternativo (intestatario conto != cliente). */
  allow_pagatore_alternativo?: boolean
  /** Blacklist regioni bloccate (MAIUSCOLO). Vuota/assente = nessuna limitazione territoriale. */
  blocked_regions?: string[]
  /** Se true, l'operatore puo' allegare i VAS "Hera NoProblem" come step finale della firma (agenzie Adventure). */
  vas_enabled?: boolean
  /** Se true, nessuna limitazione di copertura: vendita ovunque salvo blocked_regions (es. Adventure). */
  coverage_unrestricted?: boolean
  /** Se true, e' consentito l'inserimento di un IBAN estero (non IT) con BIC/SWIFT (es. Adventure). */
  foreign_iban_enabled?: boolean
  /** Path iniziale post-login (es. /operatore/pratiche per bo_ameconviene). */
  home_path?: string
  /** Se true, l'utente accede solo alla lista pratiche (no form nuova pratica). */
  pratiche_only?: boolean
}

/** Stato selezione VAS "Hera NoProblem" per una submission. */
export interface VasSelection {
  vas_enabled?: boolean
  ha_luce?: boolean
  ha_gas?: boolean
  vas_luce: boolean
  vas_gas: boolean
  vas_luce_id?: number | null
  vas_gas_id?: number | null
}

export interface OperatorLoginResponse {
  user: OperatorUser
  session_token: string
}

export interface OperatorMeResponse {
  user: OperatorUser
  session: {
    created_at: string
    expires_at: string
  }
}

export const operatorApi = {
  /**
   * Login operatore
   * @param username Username SiDial
   * @param password Password SiDial
   */
  async login(username: string, password: string): Promise<OperatorLoginResponse> {
    const response = await apiFetch<OperatorLoginResponse>('/operator/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    return response.data
  },

  /**
   * Logout operatore
   */
  async logout(): Promise<void> {
    await apiFetch('/operator/logout', {
      method: 'POST',
    })
  },

  /**
   * Ottieni dati operatore corrente
   * Restituisce null se non autenticato (invece di throw)
   */
  async getMe(): Promise<OperatorMeResponse | null> {
    try {
      const response = await apiFetch<OperatorMeResponse>('/operator/me')
      return response.data
    } catch (error) {
      // Se 401, l'utente non è autenticato
      if (error instanceof ApiError && error.statusCode === 401) {
        return null
      }
      throw error
    }
  },

  /**
   * Elenca pratiche create in modalità operatore
   * @param params Filtri e paginazione
   */
  async listPratiche(params: ListPraticheParams = {}): Promise<ListPraticheResponse> {
    const qs = new URLSearchParams()
    qs.set('mode', 'operator') // Sempre filtra per modalità operatore
    
    if (params.status) qs.set('status', params.status)
    if (params.search) qs.set('search', params.search)
    if (params.tipo_cliente) qs.set('tipo_cliente', params.tipo_cliente)
    if (params.from) qs.set('from', params.from)
    if (params.to) qs.set('to', params.to)
    if (params.page) qs.set('page', String(params.page))
    if (params.per_page) qs.set('per_page', String(params.per_page))
    
    const response = await apiFetch<{ data: PraticaListItem[]; pagination: PaginationInfo }>(`/submissions?${qs.toString()}`)
    return {
      data: response.data.data || response.data as unknown as PraticaListItem[],
      pagination: response.pagination || { total: 0, page: 1, per_page: 20, total_pages: 1, has_more: false }
    }
  },

  /**
   * Invia link firma al cliente
   * @param submissionId ID della submission
   * @param contactType Metodo di contatto ('email' | 'phone')
   */
  async sendToClient(
    submissionId: number,
    contactType: 'email' | 'phone'
  ): Promise<SendToClientResponse> {
    const response = await apiFetch<SendToClientResponse>(`/operator/submissions/${submissionId}/send`, {
      method: 'POST',
      body: JSON.stringify({
        contact_type: contactType,
      }),
    })
    return response.data
  },

  /**
   * Ottieni stato dettagliato pratica
   * @param submissionId ID della submission
   */
  async getStatus(submissionId: number): Promise<OperatorSubmissionDetail> {
    const response = await apiFetch<OperatorSubmissionDetail>(`/operator/submissions/${submissionId}/status`)
    return response.data
  },

  /**
   * Reinvia link al cliente
   * @param submissionId ID della submission
   * @param contactType Metodo di contatto ('email' | 'phone')
   */
  async resendToClient(
    submissionId: number,
    contactType: 'email' | 'phone'
  ): Promise<SendToClientResponse> {
    const response = await apiFetch<SendToClientResponse>(`/operator/submissions/${submissionId}/resend`, {
      method: 'POST',
      body: JSON.stringify({ contact_type: contactType }),
    })
    return response.data
  },

  /**
   * Prepara la duplica di una pratica completata (solo admin/supervisor).
   * Restituisce i dati della pratica originale da precompilare nel form: la
   * nuova pratica verra' creata all'invio, collegata all'originale.
   * @param submissionId ID della pratica originale (completata)
   */
  async duplicatePratica(submissionId: number): Promise<{ duplicated_from: number; source: Partial<FormData> }> {
    const response = await apiFetch<{ duplicated_from: number; source: Partial<FormData> }>(
      `/operator/submissions/${submissionId}/duplicate`,
      { method: 'POST' }
    )
    return response.data
  },

  /**
   * Stato selezione VAS "Hera NoProblem" per una submission (solo operatore).
   */
  async getVasSelection(submissionId: number): Promise<VasSelection> {
    const response = await apiFetch<VasSelection>(`/operator/submissions/${submissionId}/vas`)
    return response.data
  },

  /**
   * Imposta/rimuove i VAS "Hera NoProblem" selezionati per la submission.
   */
  async setVasSelection(
    submissionId: number,
    selection: { luce: boolean; gas: boolean }
  ): Promise<VasSelection> {
    const response = await apiFetch<VasSelection>(`/operator/submissions/${submissionId}/vas`, {
      method: 'POST',
      body: JSON.stringify(selection),
    })
    return response.data
  },

  /**
   * Esporta pratiche in formato Excel
   * @param params Filtri e/o ID singola pratica
   */
  async exportPratiche(params: ListPraticheParams & { id?: number } = {}): Promise<void> {
    const qs = new URLSearchParams()
    if (params.id) qs.set('id', String(params.id))
    if (params.status) qs.set('status', params.status)
    if (params.search) qs.set('search', params.search)
    if (params.tipo_cliente) qs.set('tipo_cliente', params.tipo_cliente)
    if (params.from) qs.set('from', params.from)
    if (params.to) qs.set('to', params.to)

    const url = apiUrl(`/operator/export-pratiche?${qs.toString()}`)
    const response = await fetch(url, { credentials: 'include' })

    if (!response.ok) {
      throw new ApiError('Errore durante l\'export', response.status)
    }

    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition')
    let filename = 'pratiche_export.xlsx'
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }

    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(a.href)
  },
}

// ============================================
// CLIENT SIGNING API
// ============================================

export const clientSigningApi = {
  /**
   * Ottieni dati submission per firma cliente
   * @param token Token di accesso
   */
  async getSubmission(token: string): Promise<ClientSigningResponse> {
    const response = await apiFetch<ClientSigningResponse>(`/client-signing/${token}`)
    return response.data
  },

  /**
   * Aggiorna consensi cliente
   * @param token Token di accesso
   * @param consents Consensi da aggiornare
   */
  async updateConsents(
    token: string,
    consents: { consenso_marketing?: boolean; consenso_comunicazioni?: boolean }
  ): Promise<ClientSigningResponse> {
    const response = await apiFetch<ClientSigningResponse>(`/client-signing/${token}`, {
      method: 'PUT',
      body: JSON.stringify(consents),
    })
    return response.data
  },

  /**
   * Avvia processo firma
   * @param token Token di accesso
   * @param contactType Metodo per ricevere OTP
   */
  async startSigning(token: string, contactType: 'email' | 'phone'): Promise<StartClientSigningResponse> {
    const response = await apiFetch<StartClientSigningResponse>(`/client-signing/${token}/start`, {
      method: 'POST',
      body: JSON.stringify({ contact_type: contactType }),
    })
    return response.data
  },

  /**
   * Stato dei documenti d'identità lato cliente (delegato dall'operatore).
   */
  async getIdentityStatus(token: string): Promise<{
    client_must_upload_identity: boolean
    count: number
    has_fronte: boolean
    has_retro: boolean
    has_required_documents: boolean
  }> {
    const response = await apiFetch<{
      client_must_upload_identity: boolean
      count: number
      has_fronte: boolean
      has_retro: boolean
      has_required_documents: boolean
    }>(`/client-signing/${token}/identity`)
    return response.data
  },

  /**
   * Carica un'immagine di documento (fronte o retro). Comprime se serve.
   */
  async uploadIdentity(
    token: string,
    blob: Blob,
    imageType: 'fronte' | 'retro',
    filename = `identity-${imageType}.jpg`
  ): Promise<{
    identity_document_id: number
    image_type: 'fronte' | 'retro'
    count: number
    has_fronte: boolean
    has_retro: boolean
    has_required_documents: boolean
  }> {
    const IDENTITY_MAX_SIZE = 5 * 1024 * 1024
    let toUpload: Blob = blob
    if (toUpload.size > IDENTITY_MAX_SIZE) {
      const result = await compressImage(toUpload, {
        maxDimension: 1920,
        quality: 0.85,
        maxSizeBytes: IDENTITY_MAX_SIZE,
      })
      toUpload = result.blob
    }

    const fd = new globalThis.FormData()
    fd.append('file', toUpload, filename)
    fd.append('image_type', imageType)

    const response = await apiFetch<{
      identity_document_id: number
      image_type: 'fronte' | 'retro'
      count: number
      has_fronte: boolean
      has_retro: boolean
      has_required_documents: boolean
    }>(`/client-signing/${token}/identity`, {
      method: 'POST',
      body: fd,
    })
    return response.data
  },
}

// Export error class
export { ApiError }

